<script lang="ts">
  /**
   * The reveal for one corrected length — shown inline under the ribbon when its
   * chip is tapped. The sentence carries the meaning ("The watch recorded X. You
   * changed it to Y."), not the colour chips or arrow below it, which are
   * aria-hidden decoration for a low-vision owner.
   */
  import type { CorrectionRecord } from "../lib/corrections";
  import { strokeColor, contrastTextOn } from "../lib/designTokens";

  let { rec }: { rec: CorrectionRecord } = $props();

  // In-set position — the number SetDetailSheet shows. The swim-wide index would
  // send him hunting for a "Length 43" that is never displayed anywhere.
  const where = $derived(
    rec.setLabel ? `Length ${rec.lapPosInSet + 1} of ${rec.setLabel}` : `Length ${rec.lapPosInSet + 1}`,
  );
</script>

<div class="reveal" role="status" aria-live="polite">
  <p class="where">{where}</p>
  <p class="sentence">
    The watch recorded {rec.from}. You changed it to {rec.to}.
  </p>
  {#if rec.excluded}
    <p class="not-counted">This length doesn't count in your totals.</p>
  {/if}
  <div class="chips" aria-hidden="true">
    <span class="s" style:background={strokeColor(rec.from)} style:color={contrastTextOn(strokeColor(rec.from))}>
      {rec.from}
    </span>
    <span class="arrow">→</span>
    <span class="s" style:background={strokeColor(rec.to)} style:color={contrastTextOn(strokeColor(rec.to))}>
      {rec.to}
    </span>
  </div>
  <p class="how">{rec.howText}</p>
</div>

<style>
  .reveal {
    margin-top: var(--space-3);
    padding: var(--space-3);
    background: var(--bg-raised);
    border-radius: var(--radius-button);
  }
  .where {
    margin: 0 0 var(--space-1);
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
  }
  /* The sentence IS the content. The chips below are decoration and are
     aria-hidden, so nothing depends on colour or on the arrow glyph. */
  .sentence {
    margin: 0 0 var(--space-2);
    font-size: 15px;
    color: var(--text-primary);
  }
  /* The one hard case the whole feature exists to explain: a length can be
     BOTH fixed AND not counted. This line carries that fact — explicit
     primary ink and bold, same standing as .sentence, never muted. */
  .not-counted {
    margin: 0 0 var(--space-2);
    font-size: 15px;
    font-weight: 700;
    color: var(--text-primary);
  }
  .chips {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .s {
    padding: 2px var(--space-2);
    border-radius: var(--radius-sm);
    font-size: 0.85rem;
    font-weight: 600;
  }
  .arrow {
    opacity: 0.7;
    color: var(--text-muted);
  }
  .how {
    margin: var(--space-2) 0 0;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
</style>
